import { useState, useCallback, useMemo } from "react";
import { defineMessages } from "react-intl";

import withFormStore, { useForm } from "../../Form/withFormStore";
import { useFormatMessage } from "../../Intl/Context";
import { useAlert } from "../../Alerts/withAlert";
import { ALERT_TYPE_ERROR } from "../../Alerts/Alert";
import StringField from "../../Form/Field/StringField";
import Button from "../../Buttons/Button";
import useResendCooldown from "../../Authentication/useResendCooldown";
import useSmsDeliveryStatusPoll from "../../Authentication/useSmsDeliveryStatusPoll";

const AuthenticationTwoFactorChallengeForm = ({
  submitChallengeCode,
  onConfirmed,
  resendChallengeCode
}) => {
  const { showAlert } = useAlert();
  const { formState, validateForm } = useForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remainingAttemptsWarning, setRemainingAttemptsWarning] = useState(null);
  const { deliveryPending, deliveryFailed, sentAt, restartPolling } =
    useSmsDeliveryStatusPoll();
  const { secondsLeft, isCoolingDown } = useResendCooldown(sentAt);

  const t = useFormatMessage();
  const help = t(intlMessages.help);
  const codeLabel = t(intlMessages.code);
  const submitLabel = t(intlMessages.submit);
  const deliveryFailedMessage = t(intlMessages.deliveryFailed);
  const submitErrorMessage = t(intlMessages.submitError);
  const resendErrorMessage = t(intlMessages.resendError);

  const resendLabel = useMemo(() => {
    if (deliveryPending) {
      return t(intlMessages.deliveryPending);
    } else if (isCoolingDown) {
      return t(intlMessages.resendCountdown, { seconds: secondsLeft });
    } else {
      return t(intlMessages.resend);
    }
  }, [deliveryPending, isCoolingDown, secondsLeft, t]);

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();

      if (isSubmitting || !validateForm()) return;

      setIsSubmitting(true);

      try {
        const result = await submitChallengeCode(formState);
        setIsSubmitting(false);

        if (result.success) {
          onConfirmed();
        } else {
          setRemainingAttemptsWarning(result.remainingAttemptsWarning);
          showAlert({ message: result.notice, type: ALERT_TYPE_ERROR });
        }
      } catch (error) {
        setIsSubmitting(false);
        showAlert({ message: submitErrorMessage, type: ALERT_TYPE_ERROR });

        throw error;
      }
    },
    [
      isSubmitting,
      validateForm,
      submitChallengeCode,
      formState,
      onConfirmed,
      showAlert,
      submitErrorMessage
    ]
  );

  // Restart polling only once the resend completed, so the first poll of the new cycle
  // sees the freshly reset PENDING status rather than the previous attempt's outcome.
  const handleResendClicked = useCallback(async () => {
    try {
      await resendChallengeCode();
      restartPolling();
    } catch (error) {
      showAlert({ message: resendErrorMessage, type: ALERT_TYPE_ERROR });

      throw error;
    }
  }, [resendChallengeCode, resendErrorMessage, restartPolling, showAlert]);

  const resendButtonDisabled = isCoolingDown || deliveryPending;

  return (
    <form
      onSubmit={handleSubmit}
      className="box padding-md"
      data-phone-number-change-challenge-form
    >
      <p className="margin-bottom-xs text-small">{help}</p>

      {deliveryFailed && (
        <p className="text-danger text-small margin-bottom-xs">{deliveryFailedMessage}</p>
      )}

      <StringField
        fieldName="code"
        required
        label={codeLabel}
        autoComplete="one-time-code"
      />

      {remainingAttemptsWarning && (
        <p className="text-danger text-small margin-bottom-xs">
          {remainingAttemptsWarning}
        </p>
      )}

      <Button
        type="submit"
        className="btn btn-primary btn-block-md margin-bottom-xs"
        onClick={handleSubmit}
        disabled={isSubmitting}
        data-loading={isSubmitting}
        label={submitLabel}
      />

      <Button
        type="button"
        className="btn-link text-legend d-block"
        onClick={handleResendClicked}
        disabled={resendButtonDisabled}
        label={resendLabel}
      />
    </form>
  );
};

export default withFormStore(AuthenticationTwoFactorChallengeForm);

const intlMessages = defineMessages({
  help: {
    id: "twoFactor.phoneNumber.confirm.help",
    defaultMessage:
      "Saisissez le code à 6 chiffres que nous venons de vous envoyer par SMS."
  },
  code: {
    id: "twoFactor.phoneNumber.confirm.code",
    defaultMessage: "Code de vérification"
  },
  submit: {
    id: "twoFactor.phoneNumber.confirm.submit",
    defaultMessage: "Valider mon numéro"
  },
  resend: {
    id: "twoFactor.phoneNumber.confirm.resend",
    defaultMessage: "Renvoyer le code"
  },
  resendCountdown: {
    id: "twoFactor.phoneNumber.confirm.resendCountdown",
    defaultMessage: "Vous pourrez demander un nouveau code dans {seconds} secondes ..."
  },
  deliveryPending: {
    id: "twoFactor.phoneNumber.confirm.deliveryPending",
    defaultMessage: "Votre code est en cours d'envoi ..."
  },
  deliveryFailed: {
    id: "twoFactor.phoneNumber.confirm.deliveryFailed",
    defaultMessage:
      "Nous n'avons pas pu envoyer votre code. Merci de réessayer dans quelques instants."
  },
  submitError: {
    id: "twoFactor.phoneNumber.confirm.submitError",
    defaultMessage: `Une erreur est survenue lors de la validation du code. Merci de 
      réessayer dans quelques instants. Si l'erreur persiste, merci de contacter le 
      support.`
  },
  resendError: {
    id: "twoFactor.phoneNumber.confirm.resendError",
    defaultMessage: `Une erreur est survenue lors de la demande d'un nouveau code. 
      Merci de réessayer dans quelques instants. Si l'erreur persiste, merci de contacter 
      le support.`
  }
});
